import React, { useContext, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  AiFillCaretUp,
  AiFillCaretDown,
  AiOutlineDelete,
  AiOutlineInfoCircle,
  AiOutlineForm,
  AiFillFilePdf,
} from 'react-icons/ai';
import Pagination from '../Pagination/Pagination';
import Text from '../Text/Text';
import './style.css';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AutoComplete from '../AutoComplete/AutoComplete';
import SelectBulkAction from './SelectBulkAction';
import { useDownloadExcel } from '../../../helpers/excel/use-excel';
import AxiosInstance from '../../../helpers/AxiosInstance';
import Swal from 'sweetalert2';
import { AuthContext } from '../../../Main';
/** Rows per request — smaller chunks = progress updates more often in the UI. */
const STUDENT_IMPORT_CHUNK_SIZE = 150;

const copyExcelRowValues = (sourceRow, targetRow) => {
  sourceRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    targetRow.getCell(colNumber).value = cell.value;
  });
};

const getData = (attribute, item) => {
  let result = item;
  for (let object of attribute.nestedObject) {
    if (!result && !object) return null;
    result = result[object];
  }

  if (Array.isArray(result)) return result.map((res) => res[attribute.name]);
  return result[attribute.name];
};

const Table = ({
  attributes,
  data,
  actions,
  numberOfPage,
  query,
  setQuery,
  textButton,
  linkButton,
  displayModal,
  role,
  establishments = [],
  establishmentSearch = null,
  setEstablishmentSearch = null,
  region = '',
  setRegion = null,
  name, //name of the collection
  title = '',
  studentIsActivatedSearch = null,
  setStudentIsActivatedSearch = null,
  setEstablishmentIsActivited = null,
  establishmentIsActivited = '',
  isDisplayed = null,
  setIsDisplayed = null,
  pdfTitle = 'table',
  exportPdf,
  setLoading = null,
  search = null,
}) => {
  console.log('Table component is loading...');
  const { userRole } = useContext(AuthContext);
  const location = useLocation();
  const history = useHistory();
  const tableRef = useRef(null);
  const [listCheckboxIds, setListCheckboxIds] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportProgress, setShowImportProgress] = useState(false);
  const [importProgress, setImportProgress] = useState({
    phase: 'idle',
    processed: 0,
    total: 0,
    chunkCurrent: 0,
    chunkTotal: 0,
    awaitingServer: false,
    chunkRowFrom: 0,
    chunkRowTo: 0,
  });
  const { t, i18n } = useTranslation();

  const changeRoute = (link) => {
    history.push({
      pathname: link,
    });
  };
  const changeAllListCheckboxIds = (value) => {
    if (value) {
      setListCheckboxIds(data.map((el) => el.id));
    } else {
      setListCheckboxIds([]);
    }
  };

  const changeOneItem = (value, id) => {
    if (value) {
      setListCheckboxIds((prev) => [...prev, id]);
    } else {
      setListCheckboxIds((prev) => prev.filter((el) => el !== id));
    }
  };

  const downloadExcel = () => {
    let fileUrl = `${location.pathname}/export?order=${query.order}&keyword=${query.keyword}`;
    if (location.pathname === '/student') {
      fileUrl = fileUrl.concat(
        `&establishmentId=${establishmentSearch || ''}&isActivated=${studentIsActivatedSearch}&region=${region || ''}`
      );
    }
    if (location.pathname === '/establishment') {
      fileUrl = fileUrl.concat(`&isActivated=${establishmentIsActivited}&region=${region || ''}`);
    }
    console.log({ fileUrl });
    AxiosInstance.get(fileUrl, { responseType: 'blob' })
      .then((blob) => {
        console.log({ blob });
        const filename = `${name}-${Date()}.xlsx`;
        const _url = window.URL || window.webkitURL;

        const url = _url.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => console.error(error));
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const resetInput = () => {
      event.target.value = '';
    };

    const parsePayload = (response) => {
      const api = response?.data || {};
      return api?.data ?? api;
    };

    try {
      if (setLoading) setLoading(true);
      setShowImportProgress(true);
      setImportProgress({
        phase: 'parsing',
        processed: 0,
        total: 0,
        chunkCurrent: 0,
        chunkTotal: 0,
        awaitingServer: false,
        chunkRowFrom: 0,
        chunkRowTo: 0,
      });

      const ExcelJS = (await import('exceljs')).default;
      const arrayBuffer = await file.arrayBuffer();
      const srcWb = new ExcelJS.Workbook();
      await srcWb.xlsx.load(arrayBuffer);

      const worksheet = srcWb.getWorksheet(1) || (srcWb.worksheets && srcWb.worksheets[0]);
      if (!worksheet) {
        throw new Error("Aucune feuille trouvée dans ce fichier Excel.");
      }

      const dataRowCount = Math.max(0, worksheet.rowCount - 1);
      if (dataRowCount === 0) {
        throw new Error("Aucune ligne de données à importer (le fichier ne contient que l'en-tête ou est vide).");
      }

      const chunkTotal = Math.max(1, Math.ceil(dataRowCount / STUDENT_IMPORT_CHUNK_SIZE));

      setImportProgress({
        phase: 'uploading',
        processed: 0,
        total: dataRowCount,
        chunkCurrent: 0,
        chunkTotal,
        awaitingServer: false,
        chunkRowFrom: 0,
        chunkRowTo: 0,
      });

      let aggregateTotal = 0;
      let aggregateSuccess = 0;
      let aggregateSkipped = 0;
      let aggregateErrors = 0;
      const aggregateErrorRows = [];

      for (let c = 0; c < chunkTotal; c++) {
        const startIdx = c * STUDENT_IMPORT_CHUNK_SIZE;
        const endIdx = Math.min(startIdx + STUDENT_IMPORT_CHUNK_SIZE, dataRowCount);

        flushSync(() => {
          setImportProgress((prev) => ({
            ...prev,
            chunkCurrent: c + 1,
            phase: 'uploading',
            awaitingServer: false,
            chunkRowFrom: startIdx + 1,
            chunkRowTo: endIdx,
          }));
        });

        const outWb = new ExcelJS.Workbook();
        const outWs = outWb.addWorksheet(worksheet.name || 'Sheet1');
        copyExcelRowValues(worksheet.getRow(1), outWs.getRow(1));

        let outRowNum = 2;
        for (let k = startIdx; k < endIdx; k++) {
          copyExcelRowValues(worksheet.getRow(k + 2), outWs.getRow(outRowNum));
          outRowNum++;
        }

        const buffer = await outWb.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const baseName = file.name.replace(/\.(xlsx|xls)$/i, '');
        const chunkFile = new File(
          [blob],
          `${baseName}-lot${c + 1}.xlsx`,
          { type: blob.type }
        );

        const formData = new FormData();
        formData.append('file', chunkFile);

        flushSync(() => {
          setImportProgress((prev) => ({
            ...prev,
            awaitingServer: true,
          }));
        });

        const response = await AxiosInstance.post('/student/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        const payload = parsePayload(response);
        const chunkTotalRows = typeof payload?.total === 'number' ? payload.total : endIdx - startIdx;
        const chunkSuccess = typeof payload?.success === 'number' ? payload.success : chunkTotalRows;
        const chunkSkipped = typeof payload?.skipped === 'number' ? payload.skipped : 0;
        const chunkErrs = typeof payload?.errors === 'number' ? payload.errors : 0;
        const chunkErrorRows = Array.isArray(payload?.errorRows) ? payload.errorRows : [];

        aggregateTotal += chunkTotalRows;
        aggregateSuccess += chunkSuccess;
        aggregateSkipped += chunkSkipped;
        aggregateErrors += chunkErrs;
        // Remap sheet row numbers to the original file (each chunk restarts at row 1).
        chunkErrorRows.forEach((er) => {
          const local = er.rowNumber;
          aggregateErrorRows.push({
            ...er,
            rowNumber: local != null ? startIdx + local : local,
          });
        });

        flushSync(() => {
          setImportProgress((prev) => ({
            ...prev,
            processed: endIdx,
            total: dataRowCount,
            chunkCurrent: c + 1,
            awaitingServer: false,
          }));
        });
      }

      setShowImportProgress(false);
      setImportProgress({
        phase: 'idle',
        processed: 0,
        total: 0,
        chunkCurrent: 0,
        chunkTotal: 0,
        awaitingServer: false,
        chunkRowFrom: 0,
        chunkRowTo: 0,
      });

      const total = aggregateTotal;
      const successCount = aggregateSuccess;
      const errorCount = aggregateErrors;
      const skippedCount = aggregateSkipped;
      const errorRows = aggregateErrorRows;

      console.log('Import response (aggregated chunks)', {
        total,
        successCount,
        errorCount,
        skippedCount,
      });

      if (typeof total === 'number' || typeof successCount === 'number' || typeof errorCount === 'number') {
        let errorRowsHTML = '';
        if (errorRows.length > 0) {
          errorRowsHTML = '<div style="margin-top: 15px; text-align: left;">';
          errorRowsHTML += '<p style="font-weight: bold; margin-bottom: 8px;">Lignes en erreur:</p>';
          errorRowsHTML +=
            '<div style="max-height: 200px; overflow-y: auto; border: 1px solid #ddd; padding: 10px; border-radius: 4px; background-color: #f9f9f9;">';

          const rowsToShow = errorRows.slice(0, 20);
          rowsToShow.forEach((errorRow) => {
            errorRowsHTML += `<div style="margin-bottom: 8px; padding: 5px; border-bottom: 1px solid #eee;">`;
            errorRowsHTML += `<strong>Ligne ${errorRow.rowNumber}:</strong> `;
            errorRowsHTML += `<span style="color: #d32f2f;">${errorRow.error || 'Erreur inconnue'}</span>`;
            if (errorRow.studentName && errorRow.studentName !== 'Unknown') {
              errorRowsHTML += ` <span style="color: #666;">(${errorRow.studentName})</span>`;
            }
            errorRowsHTML += `</div>`;
          });

          if (errorRows.length > 20) {
            errorRowsHTML += `<div style="margin-top: 8px; color: #666; font-style: italic;">... et ${
              errorRows.length - 20
            } erreur(s) supplémentaire(s)</div>`;
          }

          errorRowsHTML += '</div></div>';
        }

        Swal.fire({
          title: t('table.import_finished_title'),
          html: `
            <p>Total : ${total ?? '-'} lignes</p>
            <p style="color: green">Succès : ${successCount ?? '-'} </p>
            <p style="color: orange">Ignorés (déjà existants) : ${skippedCount ?? '-'} </p>
            <p style="color: red">Erreurs : ${errorCount ?? '-'} </p>
            ${errorRowsHTML}
          `,
          icon: 'info',
          width: errorRows.length > 0 ? '700px' : '500px',
          customClass: {
            popup: 'import-result-modal',
          },
        });
      } else {
        Swal.fire({
          title: 'Import effectué',
          text: 'Le fichier a été traité.',
        });
      }

      if (search) search();
    } catch (error) {
      console.error('Import error:', error);
      console.error('Error response:', error?.response);
      console.error('Error data:', error?.response?.data);

      setShowImportProgress(false);
      setImportProgress({
        phase: 'idle',
        processed: 0,
        total: 0,
        chunkCurrent: 0,
        chunkTotal: 0,
        awaitingServer: false,
        chunkRowFrom: 0,
        chunkRowTo: 0,
      });

      let messageText = "Erreur lors de l'importation du fichier Excel";
      let errorDetails = '';

      if (error?.response?.data) {
        const data = error.response.data;
        const serverMsg = data.message;

        if (Array.isArray(serverMsg)) {
          messageText = serverMsg.join(', ');
        } else if (typeof serverMsg === 'string') {
          messageText = serverMsg;
        }

        if (data.data) {
          errorDetails = `\n\nDétails: ${JSON.stringify(data.data)}`;
        }

        if (data.data?.errors !== undefined) {
          errorDetails += `\n\nTotal erreurs: ${data.data.errors}`;
        }
      } else if (error?.message) {
        messageText = error.message;
      }

      Swal.fire({
        title: t('table.import_error_title'),
        html: `${messageText}${errorDetails}`,
        icon: 'error',
        width: '600px',
      });
    } finally {
      if (setLoading) setLoading(false);
      resetInput();
    }
  };
  
  console.log('handleImportExcel function is defined:', typeof handleImportExcel);
  
  useEffect(() => {
    setListCheckboxIds([]);
  }, [query]);

  return (
    <div className="card-body">
      <nav
        className={`navbar navbar-light bg-light ${
          title.length > 0 ? 'justify-content-start flex-column align-items-start' : 'justify-content-end'
        }`}
      >
        <div className="w-100 d-flex justify-content-between align-items-center">
          {title.length > 0 && (
            <div>
              <h1>{title}</h1>
            </div>
          )}
          {/* {location.pathname.includes('/contract') && (
            <button
              disabled={data?.length === 0}
              type="button"
              className="btn btn-success font-weight-bold h-fit-content ml-2"
              onClick={onDownload}
            >
              Exporter excel
            </button>
          )} */}
        </div>
        <div className="d-flex">
          <div className="form-inline">
            {location.pathname.includes('/author') && (
              <AutoComplete
                className="autocomplete-search-establishment"
                placeholder={t('table.author_is_displayed')}
                onChange={(e, v) => setIsDisplayed(v ? v.value : null)}
                multiple={false}
                data={[{ name: 'tout les auteurs' }, { id: true, name: 'Oui' }, { id: false, name: 'Non' }]}
                valueObject="id"
                titleObject="name"
              />
            )}
            {['/student', '/establishment'].includes(location.pathname) && userRole !== 'ESTABLISHMENT_USER' && (
              <AutoComplete
                className="autocomplete-search-establishment"
                placeholder={t('table.region')}
                onChange={(e, v) => setRegion(v ? v.value : null)}
                multiple={false}
                data={[
                  { id: '', name: 'Toute les regions' },
                  { id: 'Nord', name: 'Nord' },
                  { id: 'Ouest', name: 'Ouest' },
                  { id: 'Est', name: 'Est' },
                  { id: 'Sud', name: 'Sud' },
                ]}
                valueObject="id"
                titleObject="name"
              />
            )}
            {/* {location.pathname.includes('/establishment') && (
              <AutoComplete
                className="autocomplete-search-establishment"
                placeholder={t('table.isActivated')}
                onChange={(e, v) => setEstablishmentIsActivited(v ? v.value : null)}
                multiple={false}
                data={[
                  { id: '', name: 'toute les statuts' },
                  { id: 'ENABLED', name: 'activé' },
                  { id: 'DISABLED', name: 'désactivé' },
                ]}
                valueObject="id"
                titleObject="name"
              />
            )} */}
            {setStudentIsActivatedSearch !== null && (
              <AutoComplete
                className="autocomplete-search-establishment"
                placeholder={t('table.isActivated')}
                onChange={(e, v) => setStudentIsActivatedSearch(v ? v.value : null)}
                multiple={false}
                data={[
                  { id: null, name: 'toute les statuts' },
                  { id: true, name: 'activé' },
                  { id: false, name: 'désactivé' },
                ]}
                valueObject="id"
                titleObject="name"
              />
            )}
            {establishments.length > 0 && (
              <AutoComplete
                className="autocomplete-search-establishment"
                placeholder={t('placeholder.establishment')}
                // name={'establishment'}
                onChange={(e, v) => setEstablishmentSearch(v ? v.value : '')}
                multiple={false}
                data={
                  region
                    ? establishments.filter((establishment) => establishment.region.toLowerCase() === region?.toLowerCase())
                    : establishments
                }
                valueObject="id"
                titleObject="name"
              />
            )}
            {
              <input
                className="form-control mr-sm-2"
                type="search"
                placeholder={t('table.search')}
                aria-label="Search"
                value={query.keyword}
                onChange={(e) => {
                  if (location.pathname.includes('/contract') && setQuery) {
                    return setQuery({ ...query, page: 0, keyword: e.target.value });
                  }
                  history.push({ search: '?' + new URLSearchParams({ ...query, page: 1, keyword: e.target.value }) });
                }}
              />
            }
          </div>
          {textButton && (
            <button
              type="button"
              className="btn btn-primary font-weight-bold"
              onClick={() => {
                displayModal ? actions.create() : changeRoute(linkButton);
              }}
            >
              {textButton}
            </button>
          )}
          {(() => {
            console.log('Checking export/import button conditions:');
            console.log('Current pathname:', location.pathname);
            console.log('Is pathname in array?', ['/book', '/student', '/establishment'].includes(location.pathname));
            console.log('Is pathname exactly /student?', location.pathname === '/student');
            return null;
          })()}
          {['/book', '/student', '/establishment'].includes(location.pathname) && (
            <div className="d-flex">
              <button
                disabled={data?.length === 0}
                type="button"
                className="btn btn-success font-weight-bold ml-2"
                onClick={() => downloadExcel()}
              >
                Exporter excel
              </button>
              {location.pathname === '/student' && (
                <button
                  type="button"
                  className="btn btn-info font-weight-bold ml-2"
                  onClick={() => {
                    setShowImportModal(true);
                  }}
                >
                  Importer excel
                </button>
              )}
            </div>
          )}
        </div>
      </nav>
      {actions.bulk && (
        <nav className="navbar-bulk-action navbar-light bg-light justify-content-start">
          <div>
            <input type="checkbox" onChange={(e) => changeAllListCheckboxIds(e.target.checked)} />
          </div>
          <div>
            <SelectBulkAction options={actions.bulk} listCheckboxIds={listCheckboxIds} />
          </div>
        </nav>
      )}
      {/* Hidden file input for Excel import */}
      {location.pathname === '/student' && (
        <input
          id="import-excel-input"
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleImportExcel}
        />
      )}
      
      {/* Import Modal */}
      {showImportModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Informations d'importation</h5>
                <button type="button" className="close" onClick={() => setShowImportModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  <strong>Avant d'importer:</strong>
                </p>
                <p className="mb-3">
                  Veuillez télécharger le modèle Excel et respecter le format du fichier. 
                  Le fichier importé doit contenir les colonnes suivantes avec les mêmes noms exacts:
                </p>
                <ul className="mb-3">
                  <li>Prénom</li>
                  <li>Nom</li>
                  <li>Date de naissance</li>
                  <li>Téléphone</li>
                  <li>Genre</li>
                  <li>Niveau</li>
                  <li>N° d'immatriculation du Baccalauréat</li>
                  <li>Établissement</li>
                </ul>
                <p className="mb-3 text-danger">
                  <strong>Important:</strong> Le format des dates doit être DD/MM/YYYY (ex: 26/11/2000)
                </p>
                <div className="text-center mb-3">
                  <a 
                    href="/assets/MODEL IMPORT.xlsx" 
                    download="MODEL IMPORT.xlsx"
                    className="btn btn-primary btn-lg"
                  >
                    <i className="fa fa-download mr-2"></i>
                    Télécharger le modèle Excel
                  </a>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowImportModal(false)}>
                  Annuler
                </button>
                <button 
                  type="button" 
                  className="btn btn-info"
                  onClick={() => {
                    setShowImportModal(false);
                    setTimeout(() => {
                      const input = document.getElementById('import-excel-input');
                      if (input) {
                        input.click();
                      }
                    }, 100);
                  }}
                >
                  Continuer l'importation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showImportModal && (
        <div className="modal-backdrop fade show" onClick={() => setShowImportModal(false)}></div>
      )}

      {showImportProgress && (
        <>
          <div
            className="modal fade show"
            style={{ display: 'block', zIndex: 1070 }}
            tabIndex="-1"
            role="dialog"
            aria-live="polite"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('table.import_in_progress_title')}</h5>
                </div>
                <div className="modal-body text-center">
                  {importProgress.phase === 'parsing' && (
                    <p className="mb-3">{t('table.import_parsing_file')}</p>
                  )}
                  {importProgress.total > 0 && (() => {
                    const {
                      total,
                      processed,
                      chunkTotal,
                      chunkCurrent,
                      awaitingServer,
                      chunkRowFrom,
                      chunkRowTo,
                    } = importProgress;
                    const rowPct = total > 0 ? (processed / total) * 100 : 0;
                    const slotPct =
                      chunkTotal > 0
                        ? ((chunkCurrent - (awaitingServer ? 0.35 : 0)) / chunkTotal) * 100
                        : 0;
                    const barPct = Math.min(
                      100,
                      Math.max(rowPct, awaitingServer ? slotPct : rowPct)
                    );
                    return (
                      <>
                        <p className="mb-2 h5">
                          {t('table.import_progress_students', {
                            processed,
                            total,
                          })}
                        </p>
                        <p className="text-muted small mb-2">{t('table.import_progress_students_hint')}</p>
                        {chunkTotal > 0 && (
                          <p className="text-muted small mb-1">
                            {t('table.import_chunk_detail', {
                              current: chunkCurrent,
                              chunks: chunkTotal,
                            })}
                          </p>
                        )}
                        {chunkRowFrom > 0 && chunkRowTo > 0 && (
                          <p className="text-muted small mb-2">
                            {t('table.import_chunk_rows', {
                              from: chunkRowFrom,
                              to: chunkRowTo,
                            })}
                          </p>
                        )}
                        {awaitingServer && (
                          <p className="text-info font-weight-bold mb-3">{t('table.import_chunk_server_busy')}</p>
                        )}
                        <div className="progress" style={{ height: '1.5rem' }}>
                          <div
                            className="progress-bar progress-bar-striped progress-bar-animated bg-info"
                            role="progressbar"
                            style={{
                              width: `${barPct}%`,
                            }}
                          />
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1065 }}
            aria-hidden="true"
          ></div>
        </>
      )}
      
      <div className="table-responsive">
        <table ref={tableRef} className="table table-striped full-table">
          <thead>
            <tr>
              {actions.bulk && <td></td>}
              {name !== 'booksList' ? <td>#</td> : <td></td>}

              {attributes.map((attribute) => {
                return (
                  <th>
                    <div className="row">
                      <div> {attribute.title} &nbsp;</div>
                      {attribute.order && (
                        <div>
                          <AiFillCaretUp
                            size="10"
                            style={
                              `${attribute.name} DESC` === query.order
                                ? { color: '#007bff' }
                                : { color: 'black', cursor: 'pointer' }
                            }
                            onClick={() => {
                              if (location.pathname.includes('/contract') && setQuery) {
                                return setQuery({ ...query, order: `${attribute.name} DESC` });
                              }
                              history.push({
                                search: '?' + new URLSearchParams({ ...query, order: `${attribute.name} DESC` }),
                              });
                            }}
                          />

                          <AiFillCaretDown
                            size="10"
                            style={
                              `${attribute.name} ASC` === query.order
                                ? { color: '#007bff' }
                                : { color: 'black', cursor: 'pointer' }
                            }
                            onClick={() => {
                              if (location.pathname.includes('/contract') && setQuery) {
                                return setQuery({ ...query, order: `${attribute.name} ASC` });
                              }
                              history.push({ search: '?' + new URLSearchParams({ ...query, order: `${attribute.name} ASC` }) });
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
            <tr></tr>
          </thead>
          {data && data.length > 0 && (
            <tbody>
              {data.map((item, index) => {
                return (
                  <tr key={item.id}>
                    {actions.bulk && (
                      <td className="checkbox-table">
                        <input
                          type="checkbox"
                          name="bulk-item"
                          checked={listCheckboxIds.findIndex((el) => el === item.id) >= 0 ? true : false}
                          onChange={(e) => changeOneItem(e.target.checked, item.id)}
                        />
                      </td>
                    )}
                    {name === 'booksList' ? <td></td> : <td>{index + 1 + query.limit * query.page}</td>}
                    {attributes.map((attribute) => {
                      const tdData = getData(attribute, item);
                      return (
                        <td>
                          <Text
                            text={attribute.translate ? t(tdData) : tdData}
                            type={attribute.type}
                            id={item.id}
                            active={attribute.active}
                          />
                        </td>
                      );
                    })}
                    {(actions.delete || actions.update || actions.details) && (
                      <td className="d-flex">
                        {actions.delete && (
                          <AiOutlineDelete
                            style={{
                              cursor: 'pointer',
                              marginRight: 5,
                              marginLeft: 5,
                              color: 'red',
                            }}
                            onClick={() => actions.delete(item.id)}
                          />
                        )}
                        {actions.update && (
                          <AiOutlineForm
                            style={{
                              cursor: 'pointer',
                              marginRight: 5,
                              marginLeft: 5,
                              color: 'green',
                            }}
                            onClick={() => {
                              displayModal ? actions.update(item.id, item.name) : actions.update(item.id);
                            }}
                          />
                        )}
                        {actions.details && (
                          <AiOutlineInfoCircle
                            style={{
                              cursor: 'pointer',
                              marginRight: 5,
                              marginLeft: 5,
                              color: 'blue',
                            }}
                            onClick={() => actions.details(item.id)}
                          />
                        )}
                        {actions.view && (
                          <AiFillFilePdf
                            style={{
                              cursor: 'pointer',
                              marginRight: 5,
                              marginLeft: 5,
                              color: 'gray',
                            }}
                            onClick={() => actions.view(item.id)}
                          />
                        )}
                        {actions.downloadContractFile && (
                          <AiFillFilePdf
                            style={{
                              cursor: 'pointer',
                              marginRight: 5,
                              marginLeft: 5,
                              color: 'gray',
                            }}
                            onClick={() => actions.downloadContractFile(item)}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>

      {data && data.length === 0 && (
        <div className="justify-content-center w-100">
          <h3 className="text-center">{t('table.empty')}</h3>
        </div>
      )}
      {data && data.length > 0 && (
        <div className="d-flex justify-content-end">
          <Pagination query={query} setQuery={setQuery} numberOfPage={numberOfPage} />
        </div>
      )}
    </div>
  );
};

export default Table;
